import React, { useState } from 'react';
import { Box, Container, Avatar, Typography, TextField, Button, InputAdornment, IconButton } from '@mui/material';
import { School as SchoolIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function InstitutionLoginPage({ onLogin }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/institution/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        toast.error(data.message || `Login failed: ${response.status}`);
        return;
      }

      const data = await response.json();
      if (data.success) {
        localStorage.setItem('gogig_institution_access', data.accessToken);
        localStorage.setItem('gogig_institution_refresh', data.refreshToken);
        localStorage.setItem('gogig_institution_data', JSON.stringify(data.institution));
        toast.success('Login successful!');
        onLogin(data.institution);
      } else {
        toast.error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        toast.error('Cannot connect to server. Please ensure the backend is running.');
      } else {
        toast.error(error.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '50%',
          height: '60%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(13, 148, 136, 0.2) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-15%',
          left: '-10%',
          width: '45%',
          height: '50%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(45, 212, 191, 0.15) 0%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.98)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255,255,255,0.1)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Avatar
            sx={{
              mb: 2,
              width: 64,
              height: 64,
              background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
              boxShadow: '0 8px 24px rgba(13, 148, 136, 0.35)',
            }}
          >
            <SchoolIcon sx={{ fontSize: 32 }} />
          </Avatar>

          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>
            Institution Login
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Access your institution dashboard
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  '&:hover': { bgcolor: '#f1f5f9' },
                  '&.Mui-focused': { bgcolor: '#fff' },
                  '&.Mui-focused fieldset': { borderColor: '#0d9488', borderWidth: 2 },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#0d9488' },
              }}
            />
            <TextField
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              required
              value={formData.password}
              onChange={handleChange}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  '&:hover': { bgcolor: '#f1f5f9' },
                  '&.Mui-focused': { bgcolor: '#fff' },
                  '&.Mui-focused fieldset': { borderColor: '#0d9488', borderWidth: 2 },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#0d9488' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                boxShadow: '0 4px 14px rgba(13, 148, 136, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                  boxShadow: '0 6px 20px rgba(13, 148, 136, 0.4)',
                },
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </Box>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              textAlign: 'center',
              bgcolor: '#f8fafc',
              borderRadius: 2,
              width: '100%',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Contact your administrator for login credentials
            </Typography>
          </Box>
        </Box>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'rgba(255,255,255,0.6)' }}>
          GoGig · Institution Portal
        </Typography>
      </Container>
    </Box>
  );
}

export default InstitutionLoginPage;
